import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

const ReviewsSection = styled.section`
  padding: 7rem 2rem;
  background: linear-gradient(135deg, #0a0a12 0%, #000000ff 50%, #111112ff 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 15% 50%, rgba(0, 245, 255, 0.08), transparent 50%),
      radial-gradient(circle at 85% 50%, rgba(18, 85, 253, 0.07), transparent 50%);
    animation: breathe 18s ease-in-out infinite;
    pointer-events: none;
  }

  @keyframes breathe {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  @media (max-width: 768px) {
    padding: 5rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 4rem;
  font-weight: 900;
  background: linear-gradient(120deg, #ffffffff, #6993ffff, #00eeffff);
  background-size: 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: flow 14s ease infinite;
  margin-bottom: 0.8rem;

  @keyframes flow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (max-width: 768px) {
    font-size: 2.6rem;
  }
`;

const Subtitle = styled.p`
  color: #bbbbff;
  font-size: 1.15rem;
  font-weight: 300;
  max-width: 620px;
  margin: 0 auto 3.5rem;
  line-height: 1.65;

  @media (max-width: 768px) {
    font-size: 1.05rem;
    margin-bottom: 2.8rem;
  }
`;

const Carousel = styled.div`
  position: relative;
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;

  @media (max-width: 768px) {
    height: 350px;
  }
`;

const CardWrapper = styled.div`
  position: absolute;
  width: 340px;
  transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  cursor: grab;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    width: 300px;
  }
`;

const Card = styled.div`
  background: rgba(22, 25, 40, 0.75);
  border-radius: 24px;
  padding: 1.75rem;
  height: 100%;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(100, 150, 255, 0.2);
  transform-style: preserve-3d;
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  ${({ $isActive }) =>
    $isActive
      ? `
    transform: translateZ(60px) scale(1);
    opacity: 1;
    z-index: 10;
  `
      : `
    transform: translateZ(0) scale(0.88);
    opacity: 0.6;
    filter: brightness(0.8);
    z-index: 5;
  `}
`;

const Avatar = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
`;

const Name = styled.div`
  font-weight: 700;
  color: white;
  font-size: 1.05rem;
  line-height: 1.3;
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-size: 0.875rem;
  opacity: 0.85;
`;

const Handle = styled.a`
  color: white;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Category = styled.span`
  color: white;
  opacity: 0.7;
`;

const Text = styled.p`
  color: #e0e0ff;
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 1rem 0 0.75rem;
  font-weight: 400;
  flex-grow: 1;
`;

const Metrics = styled.div`
  display: flex;
  gap: 0.75rem;
  font-size: 0.8rem;
  color: #00f5ff;
  font-weight: 600;
  margin-top: auto;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#00f5ff" : "rgba(255,255,255,0.2)")};
  border: none;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: #00f5ff;
    transform: scale(1.3);
  }
`;

const ReviewsCarousel = () => {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const total = 5;
  const timeoutRef = useRef(null);

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % total);
    }, 4500);
    return () => resetTimeout();
  }, [index]);

  const touchStart = useRef(0);
  const handleTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
    resetTimeout();
  };
  const handleTouchEnd = (e) => {
    const delta = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      delta > 0 ? setIndex((i) => (i + 1) % total) : setIndex((i) => (i - 1 + total) % total);
    }
  };

  // Данные отзывов — категории жёстко заданы (не переводятся)
  const reviews = [
    {
      name: "Piyush Pokharkar",
      handle: "@PiyushPokharka1",
      avatar: "https://pbs.twimg.com/profile_images/1969703860194213888/xrCXPxMe.jpg",
      text: t("review_0_text"),
      metrics: t("review_0_metrics"),
      category: "AI Builder",
    },
    {
      name: "Aakash Kanojiya",
      handle: "@Kanojiyaaakash1",
      avatar: "https://pbs.twimg.com/profile_images/1916008025782939648/tKLkY3p3.jpg",
      text: t("review_1_text"),
      metrics: t("review_1_metrics"),
      category: "No-Code",
    },
    {
      name: "Arya Soni",
      handle: "@aryasoni98",
      avatar: "https://pbs.twimg.com/profile_images/1825258061054808064/t3qUalqa.jpg",
      text: t("review_2_text"),
      metrics: t("review_2_metrics"),
      category: "Full-Stack AI",
    },
    {
      name: "John Rush",
      handle: "@johnrushx",
      avatar: "https://pbs.twimg.com/profile_images/1466385933612240901/qNMrMDlG.jpg",
      text: t("review_3_text"),
      metrics: t("review_3_metrics"),
      category: "Non-Coder",
    },
    {
      name: "John Rush",
      handle: "@johnrushx",
      avatar: "https://pbs.twimg.com/profile_images/1466385933612240901/qNMrMDlG.jpg",
      text: t("review_5_text"),
      metrics: t("review_5_metrics"),
      category: "Fullstack AI",
    },
  ];

  return (
    <ReviewsSection
      onMouseEnter={resetTimeout}
      onMouseLeave={() => {
        timeoutRef.current = setTimeout(() => {
          setIndex((i) => (i + 1) % total);
        }, 4500);
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        style={{ zIndex: 2, maxWidth: "800px", padding: "0 2rem", margin: "0 auto" }}
      >
        <Container>
          <Title>{t('reviews_title')}</Title>
          <Subtitle>{t('reviews_subtitle')}</Subtitle>

          <Carousel onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            {reviews.map((rev, i) => {
              const diff = ((i - index + total) % total);
              let position = 0;

              if (diff === 0) position = 0;
              else if (diff === 1 || diff === total - 1) position = diff === 1 ? 1 : -1;
              else return null;

              return (
                <CardWrapper
                  key={i}
                  style={{
                    transform: `translateX(${position * 380}px)`,
                  }}
                >
                  <Card $isActive={position === 0}>
                    <div style={{ display: "flex", gap: "0.9rem", alignItems: "center" }}>
                      <Avatar src={rev.avatar} alt={rev.name} />
                      <UserInfo>
                        <Name>{rev.name}</Name>
                        <MetaLine>
                          <Handle
                            href={`https://x.com/${(rev.handle || '').replace('@', '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {rev.handle}
                          </Handle>
                          <span>•</span>
                          <Category>{rev.category}</Category>
                        </MetaLine>
                      </UserInfo>
                    </div>
                    <Text>"{rev.text}"</Text>
                    <Metrics>
                      {rev.metrics.split(' · ').map((m, idx) => (
                        <span key={idx}>{m}</span>
                      ))}
                    </Metrics>
                  </Card>
                </CardWrapper>
              );
            })}
          </Carousel>

          <Dots>
            {reviews.map((_, i) => (
              <Dot key={i} active={i === index} onClick={() => setIndex(i)} />
            ))}
          </Dots>
        </Container>
      </motion.div>
    </ReviewsSection>
  );
};

export default ReviewsCarousel;